import accounting from 'accounting';
import { costRecoveryHandler } from '../../utilities/cost-recovery-handler';
import weightTypes from '../../enums/weight-types';
import { offerlistingStatuses } from '../../enums';
import { createContext } from 'react';
import { OfferListing, Inventory, Trucklane, User, TruckTypeAmounts } from '../../graphql/query-result-types';
import { ProposedOfferShift } from '../offer-comparison/offer-card-v3/offer-card-types';

interface TrucklaneOfferListing extends OfferListing {
  inventory: Inventory;
}

enum TABLE_COLUMN {
  CURRENTLY = 'CURRENTLY',
  SUGGESTED = 'SUGGESTED',
  TOTAL = 'TOTAL',
}

type TableMetric = (
  tableColumn: TABLE_COLUMN,
  offers: TrucklaneOfferListing[],
  recalculation: boolean,
  shifts: ProposedOfferShift[],
  suggestedAward: boolean,
  suggestedException: boolean,
) => string;

const calculateWeight: TableMetric = (tableColumn, offers, recalculation, shifts, suggestedAward, suggestedException) => {
  let currentWeight = 0;
  let suggestedWeight = 0;
  let totalWeight = 0;

  if ([TABLE_COLUMN.CURRENTLY, TABLE_COLUMN.TOTAL].includes(tableColumn)) {
    currentWeight = offers.reduce((acc, current) => {
      if ([offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED].includes(current.status))
        return current.quantity * current.inventory?.unitGrossWeight + acc;
      return acc;
    }, 0);
  }
  if ([TABLE_COLUMN.SUGGESTED, TABLE_COLUMN.TOTAL].includes(tableColumn)) {
    suggestedWeight = offers.reduce((acc, current) => {
      if (
        suggestedAward &&
        current.suggestions?.award?.suggested &&
        ![offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED, offerlistingStatuses.IGNORED].includes(current.status)
      )
        return current.suggestions.award.quantity * current.inventory?.unitGrossWeight + acc;
      if (
        suggestedException &&
        current.suggestions?.reservePriceException?.suggested &&
        ![offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED, offerlistingStatuses.IGNORED].includes(current.status)
      ) {
        return current.suggestions.reservePriceException.quantity * current.inventory?.unitGrossWeight + acc;
      }
      return acc;
    }, 0);

    if (suggestedAward) {
      suggestedWeight += shifts.reduce((sum, shift) => {
        return sum + shift.shiftedQuantity * shift.shiftInventory?.unitGrossWeight;
      }, 0);
    }
  }
  totalWeight = currentWeight + suggestedWeight;
  switch (tableColumn) {
    case TABLE_COLUMN.CURRENTLY:
      return `${accounting.formatNumber(currentWeight)}  ${weightTypes[offers[0].weightUnit] || weightTypes.LB}`;
    case TABLE_COLUMN.SUGGESTED:
      return recalculation ? '—' : `${accounting.formatNumber(suggestedWeight)}  ${weightTypes[offers[0].weightUnit] || weightTypes.LB}`;
    case TABLE_COLUMN.TOTAL:
      return recalculation ? '—' : `${accounting.formatNumber(totalWeight)}  ${weightTypes[offers[0].weightUnit] || weightTypes.LB}`;
    default:
      return '—';
  }
};

const calculatePallets: TableMetric = (tableColumn, offers, recalculation, shifts, suggestedAward, suggestedException) => {
  let currentPallets = 0;
  let suggestedPallets = 0;
  let totalPallets = 0;

  if ([TABLE_COLUMN.CURRENTLY, TABLE_COLUMN.TOTAL].includes(tableColumn)) {
    currentPallets = offers.reduce((acc, current) => {
      if ([offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED].includes(current.status))
        return current.quantity / (current.inventory?.casesPerPallet || 1) + acc;
      return acc;
    }, 0);
  }
  if ([TABLE_COLUMN.SUGGESTED, TABLE_COLUMN.TOTAL].includes(tableColumn)) {
    suggestedPallets = offers.reduce((acc, current) => {
      if (
        suggestedAward &&
        current.suggestions?.award?.suggested &&
        ![offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED, offerlistingStatuses.IGNORED].includes(current.status)
      )
        return current.suggestions.award.quantity / (current.inventory?.casesPerPallet || 1) + acc;
      if (
        suggestedException &&
        current.suggestions?.reservePriceException?.suggested &&
        ![offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED, offerlistingStatuses.IGNORED].includes(current.status)
      ) {
        return current.suggestions.reservePriceException.quantity / (current.inventory?.casesPerPallet || 1) + acc;
      }
      return acc;
    }, 0);
    if (suggestedAward) {
      suggestedPallets += shifts.reduce((sum, shift) => {
        return sum + shift.shiftedQuantity / (shift.shiftInventory?.casesPerPallet || 1);
      }, 0);
    }
  }
  totalPallets = currentPallets + suggestedPallets;
  switch (tableColumn) {
    case TABLE_COLUMN.CURRENTLY:
      return `${accounting.formatNumber(currentPallets)}`;
    case TABLE_COLUMN.SUGGESTED:
      return recalculation ? '—' : `${accounting.formatNumber(suggestedPallets)}`;
    case TABLE_COLUMN.TOTAL:
      return recalculation ? '—' : `${accounting.formatNumber(totalPallets)}`;
    default:
      return '—';
  }
};

const calculateRevenue: TableMetric = (tableColumn, offers, recalculation, shifts, suggestedAward, suggestedException) => {
  let currentRevenue = 0;
  let suggestedRevenue = 0;
  let totalRevenue = 0;

  if ([TABLE_COLUMN.CURRENTLY, TABLE_COLUMN.TOTAL].includes(tableColumn)) {
    currentRevenue = offers.reduce((acc, current) => {
      if ([offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED].includes(current.status)) return current.totalPrice + acc;
      return acc;
    }, 0);
  }
  if ([TABLE_COLUMN.SUGGESTED, TABLE_COLUMN.TOTAL].includes(tableColumn)) {
    suggestedRevenue = offers.reduce((acc, current) => {
      if (
        suggestedAward &&
        current.suggestions?.award?.suggested &&
        ![offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED, offerlistingStatuses.IGNORED].includes(current.status)
      )
        return current.suggestions.award.quantity * (current.totalPrice / current.quantity) + acc;
      if (
        suggestedException &&
        current.suggestions?.reservePriceException?.suggested &&
        ![offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED, offerlistingStatuses.IGNORED].includes(current.status)
      ) {
        return current.suggestions.reservePriceException.quantity * (current.totalPrice / current.quantity) + acc;
      }

      return acc;
    }, 0);
    if (suggestedAward) {
      suggestedRevenue += shifts.reduce((sum, shift) => {
        return sum + shift.shiftedQuantity * shift.unitPrice;
      }, 0);
    }
  }
  totalRevenue = currentRevenue + suggestedRevenue;
  switch (tableColumn) {
    case TABLE_COLUMN.CURRENTLY:
      return `${accounting.formatMoney(currentRevenue)}`;
    case TABLE_COLUMN.SUGGESTED:
      return recalculation ? '—' : `${accounting.formatMoney(suggestedRevenue)}`;
    case TABLE_COLUMN.TOTAL:
      return recalculation ? '—' : `${accounting.formatMoney(totalRevenue)}`;
    default:
      return '—';
  }
};

// Revenue and the delivery cost actually charged against it, per column. The LANE COST and NET REVENUE rows
// both read this so they always reconcile: whatever cost is shown is exactly what net revenue subtracted.
// Cost is only charged where there is revenue to charge it against, and a suggestion only bears the cost when
// the lane isn't already active — otherwise the active column has already paid for the truck.
const laneRevenueAndCost = (
  user: User,
  offers: TrucklaneOfferListing[],
  shifts: ProposedOfferShift[],
  suggestedAward: boolean,
  suggestedException: boolean,
) => {
  let currentRevenue = 0;
  let suggestedRevenue = 0;

  offers.forEach((offer) => {
    if ([offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED].includes(offer.status)) currentRevenue += offer.totalPrice;
    if (
      suggestedAward &&
      offer.suggestions?.award?.suggested &&
      ![offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED, offerlistingStatuses.IGNORED].includes(offer.status)
    )
      suggestedRevenue += offer.suggestions.award.quantity * (offer.totalPrice / offer.quantity);
    if (
      suggestedException &&
      offer.suggestions?.reservePriceException?.suggested &&
      ![offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED, offerlistingStatuses.IGNORED].includes(offer.status)
    ) {
      suggestedRevenue += offer.suggestions.reservePriceException.quantity * (offer.totalPrice / offer.quantity);
    }
  });

  if (suggestedAward) {
    suggestedRevenue += shifts.reduce((sum, shift) => {
      return sum + shift.shiftedQuantity * shift.unitPrice;
    }, 0);
  }

  // Delivery cost scales with the trucks the load fills (rounded up); falls back to one truck when not measurable.
  const loadMax = truckLoadMax(user, offers[0]?.truckType);
  const { perCase, perShift } = loadMax ? loadUnitFns(loadMax) : { perCase: () => 0, perShift: () => 0 };
  const currentLoad = offers.reduce(
    (acc, offer) =>
      [offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED].includes(offer.status) ? acc + offer.quantity * perCase(offer) : acc,
    0,
  );
  let suggestedLoad = offers.reduce((acc, offer) => {
    if (
      suggestedAward &&
      offer.suggestions?.award?.suggested &&
      ![offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED, offerlistingStatuses.IGNORED].includes(offer.status)
    )
      return acc + offer.suggestions.award.quantity * perCase(offer);
    if (
      suggestedException &&
      offer.suggestions?.reservePriceException?.suggested &&
      ![offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED, offerlistingStatuses.IGNORED].includes(offer.status)
    )
      return acc + offer.suggestions.reservePriceException.quantity * perCase(offer);
    return acc;
  }, 0);
  if (suggestedAward) suggestedLoad += shifts.reduce((sum, shift) => sum + shift.shiftedQuantity * perShift(shift), 0);

  const trucklaneCost = offers[0]?.trucklaneCost || 0;
  const currentCost = trucklaneCost * laneTruckMultiplier(loadMax?.max, currentLoad);
  const suggestedCost = trucklaneCost * laneTruckMultiplier(loadMax?.max, suggestedLoad);
  const totalCost = trucklaneCost * laneTruckMultiplier(loadMax?.max, currentLoad + suggestedLoad);
  const totalRevenue = currentRevenue + suggestedRevenue;

  return {
    currentRevenue,
    suggestedRevenue,
    totalRevenue,
    currentCost: currentRevenue > 0 ? currentCost : 0,
    suggestedCost: currentRevenue === 0 ? suggestedCost : 0,
    totalCost: totalRevenue > 0 ? totalCost : 0,
  };
};

const calculateLaneCost: UserTableMetric = (user, tableColumn, offers, recalculation, shifts, suggestedAward, suggestedException) => {
  const { currentCost, suggestedCost, totalCost } = laneRevenueAndCost(user, offers, shifts, suggestedAward, suggestedException);
  switch (tableColumn) {
    case TABLE_COLUMN.CURRENTLY:
      return `${accounting.formatMoney(currentCost)}`;
    case TABLE_COLUMN.SUGGESTED:
      return recalculation ? '—' : `${accounting.formatMoney(suggestedCost)}`;
    case TABLE_COLUMN.TOTAL:
      return recalculation ? '—' : `${accounting.formatMoney(totalCost)}`;
    default:
      return '—';
  }
};

const calculateNetRevenue: UserTableMetric = (user, tableColumn, offers, recalculation, shifts, suggestedAward, suggestedException) => {
  const { currentRevenue, suggestedRevenue, totalRevenue, currentCost, suggestedCost, totalCost } = laneRevenueAndCost(
    user,
    offers,
    shifts,
    suggestedAward,
    suggestedException,
  );
  switch (tableColumn) {
    case TABLE_COLUMN.CURRENTLY:
      return `${accounting.formatMoney(currentRevenue - currentCost)}`;
    case TABLE_COLUMN.SUGGESTED:
      return recalculation ? '—' : `${accounting.formatMoney(suggestedRevenue - suggestedCost)}`;
    case TABLE_COLUMN.TOTAL:
      return recalculation ? '—' : `${accounting.formatMoney(totalRevenue - totalCost)}`;
    default:
      return '—';
  }
};

const calculateRecovery: TableMetric = (tableColumn, offers, recalculation, shifts, suggestedAward, suggestedException) => {
  let current = '-';
  let suggestion = '-';
  let total = null;
  let currentRecovery = { totalPrice: 0, totalCost: 0 };
  let suggestedRecovery = { totalPrice: 0, totalCost: 0 };

  if ([TABLE_COLUMN.CURRENTLY, TABLE_COLUMN.TOTAL].includes(tableColumn)) {
    offers.forEach((offer) => {
      if ([offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED].includes(offer.status))
        currentRecovery = {
          totalPrice: offer.totalPrice + currentRecovery.totalPrice,
          totalCost: offer.quantity * offer.inventory?.unitCost + currentRecovery.totalCost,
        };
    });
    current = costRecoveryHandler(currentRecovery.totalPrice, currentRecovery.totalCost);
  }
  if ([TABLE_COLUMN.SUGGESTED, TABLE_COLUMN.TOTAL].includes(tableColumn)) {
    offers.forEach((offer) => {
      if (
        suggestedAward &&
        offer.suggestions?.award?.suggested &&
        ![offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED, offerlistingStatuses.IGNORED].includes(offer.status)
      )
        suggestedRecovery = {
          totalPrice: offer.suggestions.award.quantity * (offer.totalPrice / offer.quantity) + suggestedRecovery.totalPrice,
          totalCost: offer.suggestions.award.quantity * offer.inventory?.unitCost + suggestedRecovery.totalCost,
        };
      if (
        suggestedException &&
        offer.suggestions?.reservePriceException?.suggested &&
        ![offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED, offerlistingStatuses.IGNORED].includes(offer.status)
      )
        suggestedRecovery = {
          totalPrice: offer.suggestions.reservePriceException.quantity * (offer.totalPrice / offer.quantity) + suggestedRecovery.totalPrice,
          totalCost: offer.suggestions.reservePriceException.quantity * offer.inventory?.unitCost + suggestedRecovery.totalCost,
        };
    });
    if (suggestedAward) {
      suggestedRecovery = shifts.reduce((recovery, shift) => {
        recovery.totalCost += shift.shiftedQuantity * shift.shiftInventory?.unitCost;
        recovery.totalPrice += shift.shiftedQuantity * shift.unitPrice;
        return recovery;
      }, suggestedRecovery);
    }
    suggestion = costRecoveryHandler(suggestedRecovery.totalPrice, suggestedRecovery.totalCost);
  }
  total = costRecoveryHandler(currentRecovery.totalPrice + suggestedRecovery.totalPrice, currentRecovery.totalCost + suggestedRecovery.totalCost);
  switch (tableColumn) {
    case TABLE_COLUMN.CURRENTLY:
      return current;
    case TABLE_COLUMN.SUGGESTED:
      return recalculation ? '—' : suggestion;
    case TABLE_COLUMN.TOTAL:
      return recalculation ? '—' : total;
    default:
      return '—';
  }
};

type TruckLoadMax = { mode: 'weight' | 'pallets'; max: number };

const hasAnyValue = (bucket?: TruckTypeAmounts | null): boolean => Boolean(bucket && (bucket.dry || bucket.refrigerated || bucket.frozen));

// Maximums carry an explicit mode (weight | pallets | none) plus both buckets.
// Returns the max for the given truck type under the active mode, or null when there is nothing to count against.
const truckLoadMax = (user: User, truckType?: string): TruckLoadMax | null => {
  const maximums = user?.site?.truckMaximums;
  if (!maximums) return null;
  // Prefer the persisted mode; fall back to inferring from populated buckets for records saved before `mode` existed.
  const mode = maximums.mode || (hasAnyValue(maximums.weight) ? 'weight' : hasAnyValue(maximums.pallets) ? 'pallets' : 'none');
  if (mode === 'none') return null;
  const bucket = maximums[mode];
  if (!bucket) return null;
  const key = truckType?.toLowerCase() as keyof TruckTypeAmounts;
  return { mode, max: bucket[key] };
};

// Per-case / per-shift load contribution depends on whether the site limits by weight or pallets.
const loadUnitFns = (loadMax: TruckLoadMax) => ({
  perCase: (offer: TrucklaneOfferListing) =>
    loadMax.mode === 'weight' ? offer.inventory?.unitGrossWeight || 0 : 1 / (offer.inventory?.casesPerPallet || 1),
  perShift: (shift: ProposedOfferShift) =>
    loadMax.mode === 'weight' ? shift.shiftInventory?.unitGrossWeight || 0 : 1 / (shift.shiftInventory?.casesPerPallet || 1),
});

// Truck count as displayed: the load-to-maximum ratio rounded UP to the next tenth, never down. Any load at
// all occupies part of a truck, so 0.00001 reads 0.1 and 2.00001 reads 2.1 — a sliver over a full truck is
// still a sliver that has to travel. A ratio landing exactly on a whole number stays there (1.0 is one truck,
// and only 1.1 spills into a second).
// The x10 is de-noised before the ceiling so float error can't tip an exactly-full lane onto another tenth
// (2.0000000000000004 must read 2.0, not 2.1). Returns 0 only when there is genuinely nothing to count.
const laneTruckCountDisplay = (max: number | null | undefined, load: number): number => {
  if (!max || load <= 0) return 0;
  const tenths = Math.ceil(Number(accounting.toFixed((load / max) * 10, 6)));
  return tenths > 0 ? tenths / 10 : 0.1;
};

// Trucks a lane's load requires, rounded up — a partial truck still incurs a full delivery cost. Derived from
// the displayed count so the two can never disagree: a lane showing 2.0 bills two trucks even when the raw
// division is 2.02, and the smallest sliver of load still bills one.
// Falls back to 1 when maximums aren't applicable or the load can't be measured (today's flat-cost behavior).
const laneTruckMultiplier = (max: number | null | undefined, load: number): number =>
  !max || load <= 0 ? 1 : Math.ceil(laneTruckCountDisplay(max, load));

interface PickupMinimums {
  pickupWeight?: TruckTypeAmounts | null;
  pickupPallets?: TruckTypeAmounts | null;
}

const applicableBuyerMinimums = (
  buyerMinimums?: PickupMinimums | null,
  sellerMinimums?: PickupMinimums | null,
  truckType?: string | null,
): { weightMinimum: number | null; palletMinimum: number | null } => {
  const key = truckType?.toLowerCase() as keyof TruckTypeAmounts;
  if (!buyerMinimums || !key) return { weightMinimum: null, palletMinimum: null };
  const applicable = (buyerMinimum?: number | null, sellerMinimum?: number | null) =>
    buyerMinimum == null || (sellerMinimum != null && buyerMinimum < sellerMinimum) ? null : buyerMinimum;
  return {
    weightMinimum: applicable(buyerMinimums.pickupWeight?.[key], sellerMinimums?.pickupWeight?.[key]),
    palletMinimum: applicable(buyerMinimums.pickupPallets?.[key], sellerMinimums?.pickupPallets?.[key]),
  };
};

type UserTableMetric = (
  user: User,
  tableColumn: TABLE_COLUMN,
  offers: TrucklaneOfferListing[],
  recalculation: boolean,
  shifts: ProposedOfferShift[],
  suggestedAward: boolean,
  suggestedException: boolean,
) => string;

const calculateTruckCount: UserTableMetric = (user, tableColumn, offers, recalculation, shifts, suggestedAward, suggestedException) => {
  const loadMax = truckLoadMax(user, offers[0]?.truckType);
  // No maximum configured (None), or no limit set for this truck type → nothing to count.
  if (!loadMax || !loadMax.max) return '—';

  const { perCase, perShift } = loadUnitFns(loadMax);

  const currentLoad = offers.reduce((acc, offer) => {
    if ([offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED].includes(offer.status)) return acc + offer.quantity * perCase(offer);
    return acc;
  }, 0);

  let suggestedLoad = offers.reduce((acc, offer) => {
    if (
      suggestedAward &&
      offer.suggestions?.award?.suggested &&
      ![offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED, offerlistingStatuses.IGNORED].includes(offer.status)
    )
      return acc + offer.suggestions.award.quantity * perCase(offer);
    if (
      suggestedException &&
      offer.suggestions?.reservePriceException?.suggested &&
      ![offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED, offerlistingStatuses.IGNORED].includes(offer.status)
    )
      return acc + offer.suggestions.reservePriceException.quantity * perCase(offer);
    return acc;
  }, 0);
  if (suggestedAward) {
    suggestedLoad += shifts.reduce((sum, shift) => sum + shift.shiftedQuantity * perShift(shift), 0);
  }

  // Displayed truck count stays fractional (one decimal) — the ceil rounding is only for cost.
  const trucksFor = (load: number) => `${accounting.formatNumber(laneTruckCountDisplay(loadMax.max, load), 1)}`;

  switch (tableColumn) {
    case TABLE_COLUMN.CURRENTLY:
      return trucksFor(currentLoad);
    case TABLE_COLUMN.SUGGESTED:
      return recalculation ? '—' : trucksFor(suggestedLoad);
    case TABLE_COLUMN.TOTAL:
      return recalculation ? '—' : trucksFor(currentLoad + suggestedLoad);
    default:
      return '—';
  }
};

interface LoadedTrucklanes extends Trucklane {
  offers: TrucklaneOfferListing[];
}

type MetricsFunction<T> = (
  trucklanes: LoadedTrucklanes[],
  offerShiftLaneMap: Map<string, ProposedOfferShift[]>,
  rpeTrucklanes?: LoadedTrucklanes[],
) => { current: T; projected: T; projectedExceptions: T };

const calculateMetricSoldCostRecovery: MetricsFunction<string> = (trucklanes: LoadedTrucklanes[], offerShiftLaneMap, rpeTrucklanes) => {
  let currentRecovery = { totalPrice: 0, totalCost: 0 };
  let suggestedRecovery = { totalPrice: 0, totalCost: 0 };
  let projectedExceptions = { totalPrice: 0, totalCost: 0 };
  trucklanes.forEach((trucklane) => {
    const shifts = offerShiftLaneMap.get(trucklane.id);
    if (shifts) {
      shifts.forEach((shift) => {
        suggestedRecovery = {
          totalPrice: shift.shiftedQuantity * shift.unitPrice + suggestedRecovery.totalPrice,
          totalCost: shift.shiftedQuantity * shift.shiftInventory?.unitCost + suggestedRecovery.totalCost,
        };
      });
    }
    trucklane.offers.forEach((offer) => {
      if (offer.suggestions?.award?.suggested && [offerlistingStatuses.ACTIVE].includes(offer.status))
        suggestedRecovery = {
          totalPrice: offer.suggestions.award.quantity * (offer.totalPrice / offer.quantity) + suggestedRecovery.totalPrice,
          totalCost: offer.suggestions.award.quantity * offer.inventory?.unitCost + suggestedRecovery.totalCost,
        };
      if ([offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED].includes(offer.status))
        currentRecovery = {
          totalPrice: offer.totalPrice + currentRecovery.totalPrice,
          totalCost: offer.quantity * offer.inventory?.unitCost + currentRecovery.totalCost,
        };
    });
  });
  if (rpeTrucklanes) {
    rpeTrucklanes.forEach((trucklane) => {
      trucklane.offers.forEach((offer) => {
        if (offer.suggestions?.reservePriceException?.suggested && [offerlistingStatuses.ACTIVE].includes(offer.status))
          projectedExceptions = {
            totalPrice: offer.suggestions.reservePriceException.quantity * (offer.totalPrice / offer.quantity) + projectedExceptions.totalPrice,
            totalCost: offer.suggestions.reservePriceException.quantity * offer.inventory?.unitCost + projectedExceptions.totalCost,
          };
      });
    });
  }

  return {
    current: costRecoveryHandler(currentRecovery.totalPrice, currentRecovery.totalCost),
    projected: costRecoveryHandler(
      currentRecovery.totalPrice + suggestedRecovery.totalPrice,
      currentRecovery.totalCost + suggestedRecovery.totalCost,
    ),
    projectedExceptions: costRecoveryHandler(
      currentRecovery.totalPrice + projectedExceptions.totalPrice,
      currentRecovery.totalCost + projectedExceptions.totalCost,
    ),
  };
};

const calculateMetricTrucklanes: MetricsFunction<number> = (trucklanes: LoadedTrucklanes[], offerShiftLaneMap, rpeTrucklanes) => {
  const suggestedTrucklaneSet = new Set();
  const currentTrucklaneSet = new Set();
  const rpeTrucklaneSet = new Set();
  trucklanes.forEach((trucklane) => {
    trucklane.offers.forEach((offer) => {
      if (offer.suggestions?.award?.suggested && [offerlistingStatuses.ACTIVE].includes(offer.status)) suggestedTrucklaneSet.add(trucklane.id);
      if ([offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED].includes(offer.status)) currentTrucklaneSet.add(trucklane.id);
    });
    const shifts = offerShiftLaneMap.get(trucklane.id);
    if (shifts && shifts.length > 0) {
      suggestedTrucklaneSet.add(trucklane.id);
    }
  });
  if (rpeTrucklanes) {
    rpeTrucklanes.forEach((trucklane) => {
      trucklane.offers.forEach((offer) => {
        if (offer.suggestions?.reservePriceException?.suggested && [offerlistingStatuses.ACTIVE].includes(offer.status))
          rpeTrucklaneSet.add(trucklane.id);
      });
    });
  }
  return {
    current: currentTrucklaneSet.size,
    projected: new Set([...suggestedTrucklaneSet, ...currentTrucklaneSet]).size,
    projectedExceptions: new Set([...rpeTrucklaneSet, ...currentTrucklaneSet]).size,
  };
};

const calculateMetricRevenue: MetricsFunction<string> = (trucklanes: LoadedTrucklanes[], offerShiftLaneMap, rpeTrucklanes) => {
  let suggested = 0;
  let current = 0;
  let projectedExceptions = 0;
  trucklanes.forEach((trucklane) => {
    trucklane.offers.forEach((offer) => {
      if (offer.suggestions?.award?.suggested && [offerlistingStatuses.ACTIVE].includes(offer.status))
        suggested += offer.suggestions.award.quantity * (offer.totalPrice / offer.quantity);
      if ([offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED].includes(offer.status)) current += offer.totalPrice;
    });
    const shifts = offerShiftLaneMap.get(trucklane.id);
    if (shifts) {
      shifts.forEach((shift) => {
        suggested += shift.shiftedQuantity * shift.unitPrice;
      });
    }
  });
  if (rpeTrucklanes) {
    rpeTrucklanes.forEach((trucklane) => {
      trucklane.offers.forEach((offer) => {
        if (offer.suggestions?.reservePriceException?.suggested && [offerlistingStatuses.ACTIVE].includes(offer.status))
          projectedExceptions += offer.suggestions.reservePriceException.quantity * (offer.totalPrice / offer.quantity);
      });
    });
  }
  return {
    current: accounting.formatMoney(current),
    projected: accounting.formatMoney(suggested + current),
    projectedExceptions: accounting.formatMoney(projectedExceptions + current),
  };
};

// Each lane's delivery cost is trucklaneCost × the trucks its load fills (rounded up). Lanes whose maximum
// can't be applied fall back to a single trucklaneCost, matching pre-maximums behavior. (Shifts are not
// reflected here — a pre-existing asymmetry with Net Revenue, left unchanged.)
const calculateMetricDeliveryCost = (
  user: User,
  trucklanes: LoadedTrucklanes[],
  _offerShiftLaneMap: Map<string, ProposedOfferShift[]>,
  rpeTrucklanes?: LoadedTrucklanes[],
): { current: string; projected: string; projectedExceptions: string } => {
  let current = 0;
  let projected = 0;
  // Per-lane awarded load, so the exceptions pass can add an exception's load to the same lane's awarded load
  // and round the total once. Offers in one lane share trucks, so rounding each half separately over-counts.
  const currentByLane = new Map<string, { load: number; cost: number }>();

  trucklanes.forEach((trucklane) => {
    const loadMax = truckLoadMax(user, trucklane.offers[0]?.truckType);
    const { perCase } = loadMax ? loadUnitFns(loadMax) : { perCase: () => 0 };
    const laneCost = trucklane.offers[0]?.trucklaneCost || 0;
    let currentLoad = 0;
    let suggestedLoad = 0;
    let hasCurrent = false;
    let hasSuggested = false;
    trucklane.offers.forEach((offer) => {
      if ([offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED].includes(offer.status)) {
        hasCurrent = true;
        currentLoad += offer.quantity * perCase(offer);
      }
      if (offer.suggestions?.award?.suggested && [offerlistingStatuses.ACTIVE].includes(offer.status)) {
        hasSuggested = true;
        suggestedLoad += offer.suggestions.award.quantity * perCase(offer);
      }
    });
    if (hasCurrent) {
      const laneCurrentCost = laneCost * laneTruckMultiplier(loadMax?.max, currentLoad);
      current += laneCurrentCost;
      currentByLane.set(trucklane.id, { load: currentLoad, cost: laneCurrentCost });
    }
    if (hasCurrent || hasSuggested) projected += laneCost * laneTruckMultiplier(loadMax?.max, currentLoad + suggestedLoad);
  });

  // Exceptions column: for a lane that is both awarded and carries an exception, the exception's load is added
  // to the awarded load before the multiplier, so accepting it is charged for any extra truck it needs. Awarded
  // lanes with no exception keep their current cost.
  let projectedExceptions = 0;
  const rpeLaneIds = new Set<string>();
  rpeTrucklanes?.forEach((trucklane) => {
    const loadMax = truckLoadMax(user, trucklane.offers[0]?.truckType);
    const { perCase } = loadMax ? loadUnitFns(loadMax) : { perCase: () => 0 };
    const laneCost = trucklane.offers[0]?.trucklaneCost || 0;
    let rpeLoad = 0;
    let hasRpe = false;
    trucklane.offers.forEach((offer) => {
      if (offer.suggestions?.reservePriceException?.suggested && [offerlistingStatuses.ACTIVE].includes(offer.status)) {
        hasRpe = true;
        rpeLoad += offer.suggestions.reservePriceException.quantity * perCase(offer);
      }
    });
    if (!hasRpe) return;
    rpeLaneIds.add(trucklane.id);
    projectedExceptions += laneCost * laneTruckMultiplier(loadMax?.max, (currentByLane.get(trucklane.id)?.load || 0) + rpeLoad);
  });
  currentByLane.forEach(({ cost }, laneId) => {
    if (!rpeLaneIds.has(laneId)) projectedExceptions += cost;
  });

  return {
    current: accounting.formatMoney(current),
    projected: accounting.formatMoney(projected),
    projectedExceptions: accounting.formatMoney(projectedExceptions),
  };
};

// Net revenue = revenue − delivery cost, where each lane's delivery cost scales with the trucks its load
// fills (rounded up), falling back to a single trucklaneCost when the maximum can't be applied. Shifts
// contribute to the projected load/cost here (matching the existing revenue treatment).
const calculateMetricNetRevenue = (
  user: User,
  trucklanes: LoadedTrucklanes[],
  offerShiftLaneMap: Map<string, ProposedOfferShift[]>,
  rpeTrucklanes?: LoadedTrucklanes[],
): { current: string; projected: string; projectedExceptions: string } => {
  let suggestedRev = 0;
  let currentRev = 0;
  let rpeRev = 0;
  let currentCost = 0;
  let projectedCost = 0;
  // Per-lane awarded load, so the exceptions pass can combine it with an exception's load before rounding.
  const currentByLane = new Map<string, { load: number; cost: number }>();

  trucklanes.forEach((trucklane) => {
    const loadMax = truckLoadMax(user, trucklane.offers[0]?.truckType);
    const { perCase, perShift } = loadMax ? loadUnitFns(loadMax) : { perCase: () => 0, perShift: () => 0 };
    const laneCost = trucklane.offers[0]?.trucklaneCost || 0;
    let currentLoad = 0;
    let suggestedLoad = 0;
    let hasCurrent = false;
    let hasSuggested = false;
    trucklane.offers.forEach((offer) => {
      if (offer.suggestions?.award?.suggested && [offerlistingStatuses.ACTIVE].includes(offer.status)) {
        hasSuggested = true;
        suggestedLoad += offer.suggestions.award.quantity * perCase(offer);
        suggestedRev += offer.suggestions.award.quantity * (offer.totalPrice / offer.quantity);
      }
      if ([offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED].includes(offer.status)) {
        hasCurrent = true;
        currentLoad += offer.quantity * perCase(offer);
        currentRev += offer.totalPrice;
      }
    });
    const shifts = offerShiftLaneMap.get(trucklane.id);
    if (shifts) {
      hasSuggested = true;
      shifts.forEach((shift) => {
        suggestedLoad += shift.shiftedQuantity * perShift(shift);
        suggestedRev += shift.shiftedQuantity * shift.unitPrice;
      });
    }
    if (hasCurrent) {
      const laneCurrentCost = laneCost * laneTruckMultiplier(loadMax?.max, currentLoad);
      currentCost += laneCurrentCost;
      currentByLane.set(trucklane.id, { load: currentLoad, cost: laneCurrentCost });
    }
    if (hasCurrent || hasSuggested) projectedCost += laneCost * laneTruckMultiplier(loadMax?.max, currentLoad + suggestedLoad);
  });

  // Exceptions column: a lane that is both awarded and carries an exception has the exception's load added to
  // its awarded load before the multiplier, so accepting it is charged for any extra truck it needs. Awarded
  // lanes with no exception keep their current cost.
  let projectedExceptionsCost = 0;
  const rpeLaneIds = new Set<string>();
  rpeTrucklanes?.forEach((trucklane) => {
    const loadMax = truckLoadMax(user, trucklane.offers[0]?.truckType);
    const { perCase } = loadMax ? loadUnitFns(loadMax) : { perCase: () => 0 };
    const laneCost = trucklane.offers[0]?.trucklaneCost || 0;
    let rpeLoad = 0;
    let hasRpe = false;
    trucklane.offers.forEach((offer) => {
      if (offer.suggestions?.reservePriceException?.suggested && [offerlistingStatuses.ACTIVE].includes(offer.status)) {
        hasRpe = true;
        rpeLoad += offer.suggestions.reservePriceException.quantity * perCase(offer);
        rpeRev += offer.suggestions.reservePriceException.quantity * (offer.totalPrice / offer.quantity);
      }
    });
    if (!hasRpe) return;
    rpeLaneIds.add(trucklane.id);
    projectedExceptionsCost += laneCost * laneTruckMultiplier(loadMax?.max, (currentByLane.get(trucklane.id)?.load || 0) + rpeLoad);
  });
  currentByLane.forEach(({ cost }, laneId) => {
    if (!rpeLaneIds.has(laneId)) projectedExceptionsCost += cost;
  });

  return {
    current: accounting.formatMoney(currentRev - currentCost),
    projected: accounting.formatMoney(currentRev + suggestedRev - projectedCost),
    projectedExceptions: accounting.formatMoney(currentRev + rpeRev - projectedExceptionsCost),
  };
};

const calculateMetricWeight: MetricsFunction<string> = (trucklanes: LoadedTrucklanes[], offerShiftLaneMap, rpeTrucklanes) => {
  let suggested = 0;
  let current = 0;
  let projectedExceptions = 0;
  trucklanes.forEach((trucklane) => {
    trucklane.offers.forEach((offer) => {
      if (offer.suggestions?.award?.suggested && [offerlistingStatuses.ACTIVE].includes(offer.status))
        suggested += offer.suggestions.award.quantity * offer.inventory?.unitGrossWeight;
      if ([offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED].includes(offer.status))
        current += offer.quantity * offer.inventory?.unitGrossWeight;
    });
    const shifts = offerShiftLaneMap.get(trucklane.id);
    if (shifts) {
      shifts.forEach((shift) => {
        suggested += shift.shiftedQuantity * shift.shiftInventory?.unitGrossWeight;
      });
    }
  });
  if (rpeTrucklanes) {
    rpeTrucklanes.forEach((trucklane) => {
      trucklane.offers.forEach((offer) => {
        if (offer.suggestions?.reservePriceException?.suggested && [offerlistingStatuses.ACTIVE].includes(offer.status))
          projectedExceptions += offer.suggestions.reservePriceException.quantity * offer.inventory?.unitGrossWeight;
      });
    });
  }
  const weightUnit = trucklanes[0]?.offers[0]?.weightUnit ?? weightTypes.LB;
  return {
    current: `${accounting.formatNumber(current)} ${weightTypes[weightUnit] || weightTypes.LB}`,
    projected: `${accounting.formatNumber(suggested + current)} ${weightTypes[weightUnit] || weightTypes.LB}`,
    projectedExceptions: `${accounting.formatNumber(projectedExceptions + current)} ${weightTypes[weightUnit] || weightTypes.LB}`,
  };
};

const calculateMetricPallet: MetricsFunction<string> = (trucklanes: LoadedTrucklanes[], offerShiftLaneMap, rpeTrucklanes) => {
  let suggested = 0;
  let current = 0;
  let projectedExceptions = 0;
  trucklanes.forEach((trucklane) => {
    trucklane.offers.forEach((offer) => {
      if (offer.suggestions?.award?.suggested && [offerlistingStatuses.ACTIVE].includes(offer.status))
        suggested += offer.suggestions.award.quantity / (offer.inventory?.casesPerPallet || 1);
      if ([offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED].includes(offer.status))
        current += offer.quantity / (offer.inventory?.casesPerPallet || 1);
    });
    const shifts = offerShiftLaneMap.get(trucklane.id);
    if (shifts) {
      shifts.forEach((shift) => {
        suggested += shift.shiftedQuantity / (shift.shiftInventory?.casesPerPallet || 1);
      });
    }
  });
  if (rpeTrucklanes) {
    rpeTrucklanes.forEach((trucklane) => {
      trucklane.offers.forEach((offer) => {
        if (offer.suggestions?.reservePriceException?.suggested && [offerlistingStatuses.ACTIVE].includes(offer.status))
          projectedExceptions += offer.suggestions.reservePriceException.quantity / (offer.inventory?.casesPerPallet || 1);
      });
    });
  }
  return {
    current: accounting.formatNumber(current),
    projected: accounting.formatNumber(suggested + current),
    projectedExceptions: accounting.formatNumber(projectedExceptions + current),
  };
};

// Truck count is per lane: each lane's load is divided by that lane's own maximum (which depends on
// its truck type and whether the site limits by weight or pallets), then summed across lanes.
const calculateMetricTruckCount = (
  user: User,
  trucklanes: LoadedTrucklanes[],
  offerShiftLaneMap: Map<string, ProposedOfferShift[]>,
  rpeTrucklanes?: LoadedTrucklanes[],
): { current: string; projected: string; projectedExceptions: string } => {
  let current = 0;
  let projected = 0;
  // Per-lane awarded load, so the exceptions pass can combine it with an exception's load before rounding.
  const currentByLane = new Map<string, { load: number; count: number }>();

  trucklanes.forEach((trucklane) => {
    const loadMax = truckLoadMax(user, trucklane.offers[0]?.truckType);
    // No maximum configured (None) or no limit for this truck type → this lane contributes no trucks.
    if (!loadMax || !loadMax.max) return;
    const { perCase, perShift } = loadUnitFns(loadMax);

    let laneCurrent = 0;
    let laneSuggested = 0;
    trucklane.offers.forEach((offer) => {
      if (offer.suggestions?.award?.suggested && [offerlistingStatuses.ACTIVE].includes(offer.status))
        laneSuggested += offer.suggestions.award.quantity * perCase(offer);
      if ([offerlistingStatuses.AWARDED, offerlistingStatuses.ACCEPTED].includes(offer.status)) laneCurrent += offer.quantity * perCase(offer);
    });
    const shifts = offerShiftLaneMap.get(trucklane.id);
    if (shifts) {
      shifts.forEach((shift) => {
        laneSuggested += shift.shiftedQuantity * perShift(shift);
      });
    }
    // Round per lane, then sum across lanes — trucks can't be shared between lanes. The projected figure has
    // to round the lane's COMBINED load, not add two separately rounded halves: offers within one lane do
    // share trucks, and adding round-ups would over-count (a lane with a sliver current and a sliver suggested
    // needs one truck, not two tenths' worth of each). This is also how the cost metrics combine before
    // applying the multiplier, so the two columns agree.
    const laneCurrentCount = laneTruckCountDisplay(loadMax.max, laneCurrent);
    current += laneCurrentCount;
    projected += laneTruckCountDisplay(loadMax.max, laneCurrent + laneSuggested);
    if (laneCurrent > 0) currentByLane.set(trucklane.id, { load: laneCurrent, count: laneCurrentCount });
  });

  // Exceptions column: an awarded lane that also carries an exception has the two loads added before rounding,
  // since offers in one lane share trucks.
  let projectedExceptions = 0;
  const rpeLaneIds = new Set<string>();
  if (rpeTrucklanes) {
    rpeTrucklanes.forEach((trucklane) => {
      const loadMax = truckLoadMax(user, trucklane.offers[0]?.truckType);
      if (!loadMax || !loadMax.max) return;
      const { perCase } = loadUnitFns(loadMax);
      let laneRpe = 0;
      trucklane.offers.forEach((offer) => {
        if (offer.suggestions?.reservePriceException?.suggested && [offerlistingStatuses.ACTIVE].includes(offer.status))
          laneRpe += offer.suggestions.reservePriceException.quantity * perCase(offer);
      });
      if (laneRpe <= 0) return;
      rpeLaneIds.add(trucklane.id);
      projectedExceptions += laneTruckCountDisplay(loadMax.max, (currentByLane.get(trucklane.id)?.load || 0) + laneRpe);
    });
  }
  // Awarded lanes with no exception keep their current count.
  currentByLane.forEach(({ count }, laneId) => {
    if (!rpeLaneIds.has(laneId)) projectedExceptions += count;
  });

  return {
    current: accounting.formatNumber(current, 1),
    projected: accounting.formatNumber(projected, 1),
    projectedExceptions: accounting.formatNumber(projectedExceptions, 1),
  };
};

interface TrucklaneMetric {
  title: string;
  current: string;
  projected: string;
  projectedExceptions?: string;
}

const initialMetrics = [
  { title: 'Sold Cost Recovery (SCR)', current: 'N/A', projected: '—', projectedExceptions: '—' },
  { title: 'Truck Lanes', current: 'N/A', projected: '—', projectedExceptions: '—' },
  { title: 'Revenue', current: 'N/A', projected: '-', projectedExceptions: '—' },
  { title: 'Est Delivery Cost', current: 'N/A', projected: '-', projectedExceptions: '—' },
  { title: 'Net Revenue', current: 'N/A', projected: '-', projectedExceptions: '—' },
];

// Truck Count and Lane Cost are both only meaningful when a truck maximum is configured, so the drilldown
// splices them in after Pallets rather than listing them here.
const initialDrilldownMetrics = [
  { title: 'Net Revenue', current: '—', projected: '—', projectedExceptions: '—' },
  { title: 'Weight', current: '—', projected: '—', projectedExceptions: '—' },
  { title: 'Pallets', current: '—', projected: '-', projectedExceptions: '—' },
  { title: 'Cost Recovery', current: '—', projected: '-', projectedExceptions: '—' },
];

const generateMetrics = (
  user: User,
  trucklanes: LoadedTrucklanes[],
  metricType: TrucklaneMetric[],
  recalculation: boolean,
  offerShiftLaneMap: Map<string, ProposedOfferShift[]>,
  rpeTrucklanes?: LoadedTrucklanes[],
) => {
  const mappedMetrics = metricType.map((metric) => {
    let metricData;
    switch (metric.title) {
      case 'Sold Cost Recovery (SCR)':
      case 'Cost Recovery':
        metricData = calculateMetricSoldCostRecovery(trucklanes, offerShiftLaneMap, rpeTrucklanes);
        break;
      case 'Truck Lanes':
        metricData = calculateMetricTrucklanes(trucklanes, offerShiftLaneMap, rpeTrucklanes);
        break;
      case 'Revenue':
        metricData = calculateMetricRevenue(trucklanes, offerShiftLaneMap, rpeTrucklanes);
        break;
      // Two titles for the same figure: the lane list calls it Est Delivery Cost, the drilldown Lane Cost.
      case 'Est Delivery Cost':
      case 'Lane Cost':
        metricData = calculateMetricDeliveryCost(user, trucklanes, offerShiftLaneMap, rpeTrucklanes);
        break;
      case 'Net Revenue':
        metricData = calculateMetricNetRevenue(user, trucklanes, offerShiftLaneMap, rpeTrucklanes);
        break;
      case 'Weight':
        metricData = calculateMetricWeight(trucklanes, offerShiftLaneMap, rpeTrucklanes);
        break;
      case 'Pallets':
        metricData = calculateMetricPallet(trucklanes, offerShiftLaneMap, rpeTrucklanes);
        break;
      case 'Truck Count':
        metricData = calculateMetricTruckCount(user, trucklanes, offerShiftLaneMap, rpeTrucklanes);
        break;

      default:
        metricData = { current: '—', projected: '—', projectedExceptions: '—' };
    }
    return {
      title: metric.title,
      current: `${metricData.current}`,
      projected: recalculation ? '—' : `${metricData.projected}`,
      projectedExceptions: recalculation ? '—' : `${metricData.projectedExceptions}`,
    };
  });
  return mappedMetrics;
};

const invalidErrorCodes = ['CHANNEL_INVALID', 'CHANNEL_OUT_OF_SYNC', 'SMART_AWARD_UNKNOWN_ERROR', 'SUGGESTED_AWARDS_OUT_OF_SYNC_ERROR'];

interface TrucklaneActionInProgress {
  actionInProgress: boolean;
  setActionInProgress?: (inProgress: boolean) => void;
}

const TrucklaneActionInProgressContext = createContext<TrucklaneActionInProgress>({ actionInProgress: false, setActionInProgress: undefined });

export {
  calculateWeight,
  calculatePallets,
  calculateRevenue,
  calculateNetRevenue,
  calculateRecovery,
  calculateLaneCost,
  calculateTruckCount,
  applicableBuyerMinimums,
  laneTruckMultiplier,
  laneTruckCountDisplay,
  initialMetrics,
  initialDrilldownMetrics,
  generateMetrics,
  invalidErrorCodes,
  TrucklaneActionInProgressContext,
};
